import {
  CatalogApi,
  catalogApiRef,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import LanguageIcon from '@material-ui/icons/Language';
import { EntityGroupLinksEmptyState } from './EntityGroupLinksEmptyState';
import { LinksGridList } from './LinksGridList';
import { ColumnBreakpoints } from './types';
import { IconComponent, useApi, useApp } from '@backstage/core-plugin-api';
import {
  InfoCard,
  InfoCardVariants,
  Progress,
} from '@backstage/core-components';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { useEffect, useState } from 'react';
import {
  EntityLink,
  GroupEntity,
  RELATION_HAS_PART,
} from '@backstage/catalog-model';
import { groupLinkTranslationsRef } from './translations.ts';
import { GroupLinksV1Alpha1 } from '@internal/plugin-catalog-group-links-common';

/** @public */
export interface EntityGroupLinksCardProps {
  cols?: ColumnBreakpoints | number;
  variant?: InfoCardVariants;
}

export const EntityGroupLinksCard = (props: EntityGroupLinksCardProps) => {
  const { cols = undefined, variant } = props;
  const { entity } = useEntity();

  const app = useApp();
  const catalogApi = useApi(catalogApiRef);
  const { t } = useTranslationRef(groupLinkTranslationsRef);

  const [links, setLinks] = useState<EntityLink[] | undefined>(undefined);

  const iconResolver = (key?: string): IconComponent =>
    key ? (app.getSystemIcon(key) ?? LanguageIcon) : LanguageIcon;

  useEffect(() => {
    (async () => {
      setLinks(await get_all_group_links(entity as GroupEntity, catalogApi));
    })();
  }, [catalogApi, entity]);

  const groupLinksTitle = t('paneTitle');

  if (!links) {
    return (
      <InfoCard title={groupLinksTitle} variant={variant}>
        <Progress />
      </InfoCard>
    );
  }

  return (
    <InfoCard title={groupLinksTitle} variant={variant}>
      {links.length === 0 ? (
        <EntityGroupLinksEmptyState />
      ) : (
        <LinksGridList
          cols={cols}
          items={links.map(({ url, title, icon }) => ({
            text: title ?? url,
            href: url,
            Icon: iconResolver(icon),
          }))}
        />
      )}
    </InfoCard>
  );
};

async function get_all_group_links(group: GroupEntity, catalogApi: CatalogApi) {
  const groupLinks = getEntityRelations(group, RELATION_HAS_PART, {
    kind: 'GroupLinks',
  });

  const linksPromise = (await Promise.all(
    groupLinks.map(async ref => {
      return (await catalogApi.getEntityByRef(ref)) as
        | GroupLinksV1Alpha1
        | undefined;
    }),
  )) as (GroupLinksV1Alpha1 | undefined)[];
  return linksPromise.reduce((total, ref) => {
    if (ref) {
      total.push(...ref.spec.links);
    }
    return total;
  }, [] as EntityLink[]);
}
